import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { adminToolsKeys, useTenderEmailsQuery } from '../adminTools.queries';
import { useReprocessTenderEmailMutation } from '../adminTools.mutations';
import type { TenderEmail } from '../../../types/api';

export interface UseTenderEmailsReturn {
  dialogOpen: boolean;
  loading: boolean;
  emails: TenderEmail[];
  count: number;
  error: string;
  expandedId: string | null;
  reprocessingId: string | null;
  statusFilter: string;

  openDialog: () => Promise<void>;
  closeDialog: () => void;
  toggleExpanded: (id: string) => void;
  reprocessEmail: (id: string) => Promise<void>;
  setStatusFilter: (status: string) => void;
  refreshEmails: () => Promise<void>;
}

export function useTenderEmails(): UseTenderEmailsReturn {
  const { showError, showSuccess } = useErrorHandler();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  // Server data — fetched only while the dialog is open. statusFilter is part of
  // the query key, so changing the filter triggers a refetch.
  const emailsQuery = useTenderEmailsQuery(statusFilter, dialogOpen);
  const emails = emailsQuery.data?.emails ?? [];
  const count = emailsQuery.data?.count ?? 0;
  const loading = dialogOpen && emailsQuery.isLoading;
  const error = emailsQuery.isError ? 'Failed to load tender emails' : '';

  const reprocessMutation = useReprocessTenderEmailMutation();

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setExpandedId(null);
    setStatusFilter('');
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setExpandedId(null);
    setStatusFilter('');
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const reprocessEmail = useCallback(
    async (id: string) => {
      setReprocessingId(id);
      try {
        await reprocessMutation.mutateAsync(id);
        showSuccess('Email queued for reprocessing');
        // Refresh the list after a short delay to show updated status.
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: adminToolsKeys.tenderEmails(statusFilter) });
        }, 1500);
      } catch (err) {
        showError(err, 'Failed to reprocess email');
      } finally {
        setReprocessingId(null);
      }
    },
    [reprocessMutation, statusFilter, queryClient, showError, showSuccess]
  );

  const handleStatusFilter = useCallback((status: string) => {
    // Changing the filter updates the query key, which refetches automatically.
    setStatusFilter(status);
  }, []);

  const refreshEmails = useCallback(async () => {
    await emailsQuery.refetch();
  }, [emailsQuery]);

  return {
    dialogOpen,
    loading,
    emails,
    count,
    error,
    expandedId,
    reprocessingId,
    statusFilter,
    openDialog,
    closeDialog,
    toggleExpanded,
    reprocessEmail,
    setStatusFilter: handleStatusFilter,
    refreshEmails,
  };
}
